import { useEffect } from 'react'
import type { BadgeWithProgress } from '../../types'
import { BadgeIcon, RarityPill } from './BadgeCard'
import { RARITY, getCriteriaDescription } from './rarity'

interface BadgeDetailModalProps {
  badge: BadgeWithProgress
  onClose: () => void
}

const T = { sora: "'Sora', sans-serif", inter: "'Inter', sans-serif" }

export function BadgeDetailModal({ badge, onClose }: BadgeDetailModalProps) {
  const earned = !!badge.earned_at
  const r = RARITY[badge.rarity]
  const isLegendary = badge.rarity === 'legendary'
  const progress = badge.progress
  const earnedDate = earned
    ? new Date(badge.earned_at!).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })
    : null

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [onClose])

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(28,25,23,.55)',
          zIndex: 80,
          animation: 'fade-in .2s ease',
          backdropFilter: 'blur(1px)',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 90,
          background: '#fff',
          borderRadius: '20px 20px 0 0',
          boxShadow: '0 -8px 40px rgba(0,0,0,.2)',
          animation: 'slide-up .28s cubic-bezier(.32,0,.28,1)',
          overflow: 'hidden',
          maxWidth: 512,
          margin: '0 auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 0' }}>
          <div style={{ width: 36, height: 4, borderRadius: 9999, background: '#e7e5e4' }} />
        </div>

        {isLegendary && (
          <div
            style={{
              height: 3,
              background: 'linear-gradient(to right,#fde68a,#d97706,#fde68a)',
              margin: '10px 20px 0',
              borderRadius: 9999,
            }}
          />
        )}

        <div style={{ padding: '16px 20px 32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
            <BadgeIcon badge={badge} size={68} />
            <div>
              <div
                style={{
                  fontFamily: T.sora,
                  fontWeight: 700,
                  fontSize: 20,
                  color: '#78350f',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                  marginBottom: 6,
                }}
              >
                {badge.name}
              </div>
              <RarityPill badge={badge} />
            </div>
          </div>

          <div style={{ height: 1, background: '#fef3c7', marginBottom: 16 }} />

          <p
            style={{
              fontFamily: T.inter,
              fontSize: 14,
              color: '#7a4528',
              lineHeight: 1.6,
              marginBottom: 16,
            }}
          >
            {badge.description}
          </p>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                background: '#fffbeb',
                borderRadius: 12,
                padding: '10px 14px',
                border: '1px solid #fef3c7',
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: '#a87d52',
                  fontWeight: 600,
                  fontFamily: T.inter,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: 2,
                }}
              >
                Points
              </div>
              <div
                style={{
                  fontFamily: T.sora,
                  fontWeight: 700,
                  fontSize: 22,
                  color: '#d97706',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                +{badge.points_value}
              </div>
            </div>
            <div
              style={{
                background: isLegendary ? 'linear-gradient(135deg,#fef9c3,#fef3c7)' : '#fffbeb',
                borderRadius: 12,
                padding: '10px 14px',
                border: `1px solid ${r.border}`,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: '#a87d52',
                  fontWeight: 600,
                  fontFamily: T.inter,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: 2,
                }}
              >
                Rarity
              </div>
              <div
                style={{
                  fontFamily: T.sora,
                  fontWeight: 700,
                  fontSize: 16,
                  color: r.text,
                }}
              >
                {isLegendary ? '✦ ' : ''}
                {r.label}
              </div>
            </div>
          </div>

          <div
            style={{
              background: '#fafaf9',
              borderRadius: 12,
              padding: '10px 14px',
              border: '1px solid #f5f5f4',
              marginBottom: 16,
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: '#78716c',
                fontWeight: 600,
                fontFamily: T.inter,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                marginBottom: 4,
              }}
            >
              How to earn
            </div>
            <div
              style={{
                fontSize: 13,
                color: '#57534e',
                fontFamily: T.inter,
                lineHeight: 1.5,
              }}
            >
              {getCriteriaDescription(badge.criteria)}
            </div>
          </div>

          {!earned && progress && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span
                  style={{
                    fontSize: 12,
                    color: '#8c5f3a',
                    fontFamily: T.inter,
                    fontWeight: 600,
                  }}
                >
                  Progress
                </span>
                <span
                  style={{
                    fontSize: 12,
                    color: '#d97706',
                    fontFamily: T.inter,
                    fontWeight: 700,
                  }}
                >
                  {progress.current} / {progress.required}
                </span>
              </div>
              <div
                style={{
                  height: 8,
                  borderRadius: 9999,
                  background: '#f5f5f4',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    borderRadius: 9999,
                    width: `${Math.round((progress.current / progress.required) * 100)}%`,
                    background: 'linear-gradient(to right,#d97706,#f59e0b)',
                  }}
                />
              </div>
            </div>
          )}

          {earned ? (
            <div
              style={{
                background: 'linear-gradient(135deg,#dcfce7,#bbf7d0)',
                borderRadius: 12,
                padding: '10px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                border: '1px solid #86efac',
              }}
            >
              <span style={{ fontSize: 18 }}>🏅</span>
              <div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#15803d',
                    fontFamily: T.inter,
                  }}
                >
                  Earned {earnedDate}
                </div>
                <div style={{ fontSize: 11, color: '#16a34a', fontFamily: T.inter }}>
                  Saved to your profile
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={onClose}
              style={{
                width: '100%',
                padding: '12px 0',
                borderRadius: 10,
                background: '#f5f5f4',
                border: 'none',
                cursor: 'pointer',
                fontFamily: T.inter,
                fontWeight: 600,
                fontSize: 14,
                color: '#78716c',
                minHeight: 0,
              }}
            >
              🔒 Not yet earned
            </button>
          )}
        </div>
      </div>
    </>
  )
}
